/**
 * Semitransparent arc overlay showing the valid rotation range for a bone's
 * constraint on the active rotation axis.
 *
 * Only visible while the user is actively rotating on a specific axis (x/y/z)
 * in pose mode. The arc matches the gizmo ring radius and shows:
 *   - A dimmed fill for the full allowable range
 *   - A brighter fill for the currently-used portion
 *   - A prominent line marking the rest position (angle 0)
 *   - A billboarded angle label at the current-angle arc tip
 *
 * Supports hinge, ball-and-socket, and asymmetric ball constraints.
 */

import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import { getBone, getRestPose } from "../../mannequin/boneRegistry.ts";
import {
  getConstraint,
  type JointConstraint,
} from "../../mannequin/constraints.ts";
import {
  decomposeSwingTwist,
  extractAxisAngle,
  normalizeAngle,
} from "../../mannequin/quaternionMath.ts";
import { makeScratch } from "../scratch.ts";
import { RING_RADIUS } from "./constants.ts";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ARC_SEGMENTS = 64;
const ARC_OPACITY = 0.25;
const CURRENT_ARC_OPACITY = 0.45;
const REST_LINE_OPACITY = 0.7;

// Axis unit vectors
const AXIS_VECTORS: Record<string, THREE.Vector3> = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

// Colors matching gizmo axis colors
const ARC_COLORS: Record<string, THREE.Color> = {
  x: new THREE.Color(0xff4444),
  y: new THREE.Color(0x44ff66),
  z: new THREE.Color(0x4488ff),
};

// Scratch objects (used by getCurrentAngle)
const tmp = makeScratch({
  restQ: "quat",
  currentQ: "quat",
  deltaQ: "quat",
  swingQ: "quat",
  twistQ: "quat",
  axis: "vec3",
});

// ---------------------------------------------------------------------------
// Geometry builders
// ---------------------------------------------------------------------------

/**
 * Allocate a reusable sector-arc geometry with capacity for the maximum
 * segment count. Vertex 0 is the center; vertices 1..(ARC_SEGMENTS+1) are the
 * rim. The index buffer is a complete triangle fan; `drawRange` is used to
 * restrict rendering to the active segment count.
 *
 * Positions are updated in place by `updateArcGeometry` — this keeps the
 * geometry identity stable across frames, avoiding GPU-buffer churn during
 * active rotation.
 */
function createArcGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const vertCount = ARC_SEGMENTS + 2;
  const positions = new Float32Array(vertCount * 3);
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));

  const indices = new Uint16Array(ARC_SEGMENTS * 3);
  for (let i = 0; i < ARC_SEGMENTS; i++) {
    indices[i * 3] = 0;
    indices[i * 3 + 1] = i + 1;
    indices[i * 3 + 2] = i + 2;
  }
  geo.setIndex(new THREE.BufferAttribute(indices, 1));
  geo.setDrawRange(0, 0);
  return geo;
}

/**
 * Overwrite `geo`'s position buffer with a pie-slice wedge in the plane
 * perpendicular to `axis`, sweeping from `startAngle` to `endAngle`. The
 * active segment count is published via `setDrawRange`.
 */
function updateArcGeometry(
  geo: THREE.BufferGeometry,
  axis: "x" | "y" | "z",
  startAngle: number,
  endAngle: number,
): void {
  const sweep = endAngle - startAngle;
  if (Math.abs(sweep) < 1e-6) {
    geo.setDrawRange(0, 0);
    return;
  }

  const segments = Math.max(
    4,
    Math.round((ARC_SEGMENTS * Math.abs(sweep)) / (2 * Math.PI)),
  );
  const radius = RING_RADIUS;

  const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
  const positions = posAttr.array as Float32Array;

  // Center vertex at origin (index 0)
  positions[0] = 0;
  positions[1] = 0;
  positions[2] = 0;

  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startAngle + t * sweep;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const idx = (i + 1) * 3;
    if (axis === "x") {
      positions[idx] = 0;
      positions[idx + 1] = cos * radius;
      positions[idx + 2] = sin * radius;
    } else if (axis === "y") {
      positions[idx] = cos * radius;
      positions[idx + 1] = 0;
      positions[idx + 2] = sin * radius;
    } else {
      positions[idx] = cos * radius;
      positions[idx + 1] = sin * radius;
      positions[idx + 2] = 0;
    }
  }

  posAttr.needsUpdate = true;
  geo.setDrawRange(0, segments * 3);
}

/**
 * Allocate a reusable rest-line geometry (center → rim point at angle 0).
 * The rim endpoint is rewritten in place by `updateRestLineGeometry` when the
 * active axis changes.
 */
function createRestLineGeometry(): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  const positions = new Float32Array(6);
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
}

function updateRestLineGeometry(
  geo: THREE.BufferGeometry,
  axis: "x" | "y" | "z",
): void {
  const r = RING_RADIUS;
  const posAttr = geo.getAttribute("position") as THREE.BufferAttribute;
  const positions = posAttr.array as Float32Array;
  positions[0] = 0;
  positions[1] = 0;
  positions[2] = 0;
  if (axis === "x") {
    positions[3] = 0;
    positions[4] = r;
    positions[5] = 0;
  } else {
    positions[3] = r;
    positions[4] = 0;
    positions[5] = 0;
  }
  posAttr.needsUpdate = true;
}

/**
 * Compute the rim position for a given angle on a given axis (for label placement).
 */
function arcRimPosition(
  axis: "x" | "y" | "z",
  angle: number,
  scale: number,
): [number, number, number] {
  const r = RING_RADIUS * scale;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  if (axis === "x") return [0, cos * r, sin * r];
  if (axis === "y") return [cos * r, 0, sin * r];
  return [cos * r, sin * r, 0];
}

// ---------------------------------------------------------------------------
// Constraint range extraction
// ---------------------------------------------------------------------------

interface ArcRange {
  min: number;
  max: number;
}

function getConstraintRange(
  constraint: JointConstraint,
  axis: "x" | "y" | "z",
): ArcRange | null {
  if (constraint.type === "hinge") {
    if (axis !== constraint.axis) return null;
    return { min: constraint.minAngle, max: constraint.maxAngle };
  }

  if (constraint.type === "per_axis") {
    if (axis === "x") return { min: constraint.xMin, max: constraint.xMax };
    if (axis === "y") return { min: constraint.yMin, max: constraint.yMax };
    if (axis === "z") return { min: constraint.zMin, max: constraint.zMax };
  }

  return null;
}

function getCurrentAngle(
  bone: THREE.Bone,
  axis: "x" | "y" | "z",
  restPose: [number, number, number, number],
): number {
  tmp.restQ.set(restPose[0], restPose[1], restPose[2], restPose[3]);
  tmp.currentQ.copy(bone.quaternion);
  tmp.deltaQ.copy(tmp.restQ).invert().multiply(tmp.currentQ).normalize();

  tmp.axis.copy(AXIS_VECTORS[axis]);

  if (axis === "y") {
    decomposeSwingTwist(tmp.deltaQ, tmp.axis, tmp.swingQ, tmp.twistQ);
    return normalizeAngle(
      2 *
        Math.atan2(
          tmp.twistQ.x * tmp.axis.x +
            tmp.twistQ.y * tmp.axis.y +
            tmp.twistQ.z * tmp.axis.z,
          tmp.twistQ.w,
        ),
    );
  }

  return extractAxisAngle(tmp.deltaQ, tmp.axis);
}

// ---------------------------------------------------------------------------
// Angle label — canvas-based sprite texture
// ---------------------------------------------------------------------------

function createLabelCanvas() {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  return { canvas, ctx, texture };
}

function updateLabelCanvas(
  ctx: CanvasRenderingContext2D,
  texture: THREE.CanvasTexture,
  angleDeg: number,
  color: THREE.Color,
) {
  ctx.clearRect(0, 0, 256, 64);
  ctx.font = "bold 38px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  // Text shadow for readability
  ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = `#${color.getHexString()}`;
  ctx.fillText(`${angleDeg.toFixed(1)}\u00B0`, 128, 32);
  ctx.shadowColor = "transparent";
  texture.needsUpdate = true;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface RotationArcOverlayProps {
  activeHandleAxisRef: React.RefObject<"x" | "y" | "z" | null>;
}

export function RotationArcOverlay({
  activeHandleAxisRef,
}: RotationArcOverlayProps) {
  const [arcColor, setArcColor] = useState<THREE.Color>(ARC_COLORS.x);
  const [show, setShow] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);

  // Wrapper group ref — applies counter-rotation to keep arc fixed in rest-pose space
  const groupRef = useRef<THREE.Group>(null);

  // Track which axis we last built geometry for
  const builtAxisRef = useRef<string | null>(null);
  const builtBoneRef = useRef<string | null>(null);
  const prevShowRef = useRef(false);

  // Angle label
  const labelRef = useRef<THREE.Sprite>(null);

  // Component-lifetime owned disposables. useMemo allocates them once with
  // stable identity; the useEffect below owns their release. Geometries are
  // preallocated at maximum capacity and updated in place every frame —
  // avoiding the per-frame BufferGeometry / GPU-buffer churn that active
  // rotation would otherwise generate.
  const owned = useMemo(() => {
    const label = createLabelCanvas();
    const labelMat = new THREE.SpriteMaterial({
      map: label.texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const restLineMat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: REST_LINE_OPACITY,
      depthTest: false,
      depthWrite: false,
    });
    const rangeArcGeo = createArcGeometry();
    const currentArcGeo = createArcGeometry();
    const restLineGeo = createRestLineGeometry();
    return {
      labelCtx: label.ctx,
      labelTexture: label.texture,
      labelMat,
      restLineMat,
      rangeArcGeo,
      currentArcGeo,
      restLineGeo,
    };
  }, []);
  const {
    labelCtx,
    labelTexture,
    labelMat,
    restLineMat,
    rangeArcGeo,
    currentArcGeo,
    restLineGeo,
  } = owned;

  useEffect(() => {
    return () => {
      owned.labelTexture.dispose();
      owned.labelMat.dispose();
      owned.restLineMat.dispose();
      owned.rangeArcGeo.dispose();
      owned.currentArcGeo.dispose();
      owned.restLineGeo.dispose();
    };
  }, [owned]);

  useFrame(() => {
    const activeAxis = activeHandleAxisRef.current;
    const state = useStore.getState();
    const poseMode = state.poseMode;
    const commandBuffer = state.commandBuffer;

    const isActive =
      activeAxis != null &&
      commandBuffer?.type === "boneRotate" &&
      poseMode?.activeBone != null;

    if (!isActive) {
      if (prevShowRef.current) {
        setShow(false);
        setShowCurrent(false);
        prevShowRef.current = false;
        if (labelRef.current) labelRef.current.visible = false;
      }
      builtAxisRef.current = null;
      builtBoneRef.current = null;
      return;
    }

    const boneName = poseMode!.activeBone!;
    const objectId = poseMode!.objectId;

    const constraint = getConstraint(boneName);
    if (!constraint) {
      if (prevShowRef.current) {
        setShow(false);
        setShowCurrent(false);
        prevShowRef.current = false;
      }
      if (labelRef.current) labelRef.current.visible = false;
      return;
    }

    const range = getConstraintRange(constraint, activeAxis!);
    if (!range) {
      if (prevShowRef.current) {
        setShow(false);
        setShowCurrent(false);
        prevShowRef.current = false;
      }
      if (labelRef.current) labelRef.current.visible = false;
      return;
    }

    // Rebuild range geometry only when axis or bone changes
    if (
      builtAxisRef.current !== activeAxis ||
      builtBoneRef.current !== boneName
    ) {
      updateArcGeometry(rangeArcGeo, activeAxis!, range.min, range.max);
      updateRestLineGeometry(restLineGeo, activeAxis!);
      setArcColor(ARC_COLORS[activeAxis!] ?? ARC_COLORS.x);
      builtAxisRef.current = activeAxis;
      builtBoneRef.current = boneName;
    }

    if (!prevShowRef.current) {
      setShow(true);
      prevShowRef.current = true;
    }

    // ----- Counter-rotation: keep arc fixed in rest-pose space -----
    // Only rotate around the active axis so the arc stays coplanar with the
    // gizmo ring. The full inv(current)*rest counter-rotation drifts out of
    // the ring's plane at extreme multi-axis rotations.
    const bone = getBone(objectId, boneName);
    if (!bone) {
      setShowCurrent(false);
      if (labelRef.current) labelRef.current.visible = false;
      return;
    }

    const restPose = getRestPose(objectId, boneName);
    if (!restPose) {
      setShowCurrent(false);
      if (labelRef.current) labelRef.current.visible = false;
      return;
    }

    // Update current-angle arc every frame
    const angle = getCurrentAngle(bone, activeAxis!, restPose);
    const clampedAngle = Math.max(range.min, Math.min(range.max, angle));

    if (groupRef.current) {
      tmp.axis.copy(AXIS_VECTORS[activeAxis!]);
      groupRef.current.quaternion.setFromAxisAngle(tmp.axis, -angle);
    }

    if (Math.abs(clampedAngle) > 1e-4) {
      const start = Math.min(0, clampedAngle);
      const end = Math.max(0, clampedAngle);
      updateArcGeometry(currentArcGeo, activeAxis!, start, end);
      setShowCurrent(true);
    } else {
      setShowCurrent(false);
    }

    // Update angle label
    if (labelRef.current) {
      const absDeg = Math.abs((clampedAngle * 180) / Math.PI);
      if (absDeg > 0.1) {
        const color = ARC_COLORS[activeAxis!];
        updateLabelCanvas(
          labelCtx,
          labelTexture,
          (clampedAngle * 180) / Math.PI,
          color,
        );

        // Position at the arc tip, slightly outside the ring
        const [lx, ly, lz] = arcRimPosition(activeAxis!, clampedAngle, 1.25);
        labelRef.current.position.set(lx, ly, lz);
        labelRef.current.scale.set(0.6, 0.15, 1);
        labelRef.current.visible = true;
      } else {
        labelRef.current.visible = false;
      }
    }
  });

  return (
    <group ref={groupRef}>
      <mesh
        geometry={rangeArcGeo}
        visible={show}
        frustumCulled={false}
        renderOrder={997}
      >
        <meshBasicMaterial
          color={arcColor}
          transparent
          opacity={ARC_OPACITY}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh
        geometry={currentArcGeo}
        visible={show && showCurrent}
        frustumCulled={false}
        renderOrder={998}
      >
        <meshBasicMaterial
          color={arcColor}
          transparent
          opacity={CURRENT_ARC_OPACITY}
          depthTest={false}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Rest position line — prominent mark at angle 0 within the sector */}
      <lineSegments
        geometry={restLineGeo}
        material={restLineMat}
        visible={show}
        frustumCulled={false}
        renderOrder={999}
      />
      {/* Billboarded angle label at the current-angle arc tip */}
      <sprite
        ref={labelRef}
        material={labelMat}
        visible={false}
        frustumCulled={false}
        renderOrder={1000}
      />
    </group>
  );
}
